<template>
    <hts-slide-over
        :show="visible"
        :title="$t('labels.editDocument')"
        :submit="visibleCreate"
        :undo-enable="true"
        :rules="form.rulesEditDocument"
        @close="closeOverSide">
        <template #body>
            <hts-card :title="$t('labels.generalInformation')">
                <div class="grid gap-4 grid-cols-1 sm:grid-cols-2">
                    <hts-form-select
                        v-model="dataFormEditDocument.type"
                        :label="$t('labels.documentType')"
                        required
                        :items="listTypeDocument"
                        key-id="key"
                        key-label="value"
                        :color="color"
                        name="documentType"
                        prop="documentType"
                        :text="getLabelDocumentType(dataFormEditDocument.type)" />
                    <hts-form-input
                        v-model="dataFormEditDocument.fileName"
                        :label="$t('labels.documentFileName')"
                        :color="color"
                        name="documentFileName"
                        prop="documentFileName" />
                    <hts-form-select
                        v-if="dataFormEditDocument.type === 'HEALTHCHECK'"
                        v-model="dataFormEditDocument.healthResult"
                        required
                        :label="$t('labels.driverModule.healthCheck.value')"
                        :items="healthResults"
                        key-id="key"
                        key-label="value"
                        name="driverModule.healthCheck.value"
                        prop="healthCheck"
                        :color="color" />
                    <hts-form-date
                        v-if="dataFormEditDocument.type === 'HEALTHCHECK' && dataFormEditDocument.healthResult"
                        v-model="dataFormEditDocument.expiredDate"
                        :label="$t('labels.expiredDate')"
                        :color="color"
                        name="expiredDate"
                        prop="expiredDate" />
                    <hts-form-file
                        v-model="fileDocument"
                        type="file"
                        :label="$t('labels.document')"
                        :color="color"
                        name="document"
                        prop="document"
                        :file-name="dataFormEditDocument.fileOriginName" />
                    <hts-form-date
                        v-if="dataFormEditDocument.type === 'CONTRACT'"
                        v-model="dataFormEditDocument.expiredDate"
                        :label="$t('labels.expiredDate')"
                        :color="color"
                        name="expiredDate"
                        prop="expiredDate" />
                    <hts-form-input
                        v-model="dataFormEditDocument.description"
                        :label="$t('labels.description')"
                        :color="color"
                        name="description"
                        prop="description" />
                </div>
                <hts-modal
                    :show="openPopupConfirmDocument"
                    :title="$t('labels.confirmEditDocument')"
                    type="confirm"
                    :content="labelConfirmDocument"
                    @hide="openPopupConfirmDocument = false"
                    @confirm="handleSubmitFormEditDocument" />
            </hts-card>
        </template>
    </hts-slide-over>
</template>

<script lang="ts" setup>
import form from './imports/form'
import data from './imports/data'
const { listTypeDocument, healthResults, getLabelDocumentType } = data()

const props = defineProps({
  show: {
    type: Boolean,
    default: true,
    required: true
  },
  codeDriver: {
    type: String
  },
  color: {
    type: String,
    default: 'theme'
  },
  sizeSlideOver: {
    type: String,
    default: 'small'
  },
  column: {
    type: Number,
    default: 2
  },
  documentInfo: {
    type: Object
  }
})

const _initForm = {
  driverDocument: null,
  driver: null,
  fileName: null,
  description: null,
  type: null,
  expiredDate: null,
  healthResult: 1
} as { [key: string]: any }

const dataFormEditDocument: { [key: string]: any } = ref({ ..._initForm })
const idDocumentDriver = ref()

const open = (data: { [key: string]: any }) => {
  dataFormEditDocument.value = { ...data }
  if ($lodash.isNil(dataFormEditDocument.value.healthResult)) {
    dataFormEditDocument.value.healthResult = _initForm.healthResult
  }
  idDocumentDriver.value = data._id
  visible.value = true
}

const resetForm = () => {
  fileDocument.value = undefined
  dataFormEditDocument.value = { ...data }
}

defineExpose({
  open
})

const emits = defineEmits(['close', 'refreshTables'])
const fileDocument = ref<File | string | undefined>(undefined)

const handleSubmitFormEditDocument = async () => {
  const dataForm = new FormData()

  fileDocument.value && dataForm.append('driverDocument', fileDocument.value)
  dataFormEditDocument.value.fileName && dataForm.append('fileName', dataFormEditDocument.value.fileName)
  dataForm.append('driver', props.codeDriver || '')
  dataFormEditDocument.value.description && dataForm.append('description', dataFormEditDocument.value.description)
  dataFormEditDocument.value.type && dataForm.append('type', dataFormEditDocument.value.type)

  if (dataFormEditDocument.value.type === 'HEALTHCHECK') {
    typeof dataFormEditDocument.value.healthResult === 'number' && dataForm.append('healthResult', String(dataFormEditDocument.value.healthResult))
    dataFormEditDocument.value.expiredDate && dataForm.append('expiredDate', dataFormEditDocument.value.expiredDate)
  }

  if (dataFormEditDocument.value.type === 'CONTRACT') {
    dataFormEditDocument.value.expiredDate && dataForm.append('expiredDate', dataFormEditDocument.value.expiredDate)
  }

  const data: any = await $api($url.driver.document.update, {
    uriParams: { ID: idDocumentDriver.value },
    body: dataForm
  })

  const resData: any = data?.data?.value

  if (resData?.success) {
    process.client && $toast().success($t('notifications.editSuccessDocument'))
    emits('close')
    emits('refreshTables')
    closeOverSide()
    openPopupConfirmDocument.value = false
    visible.value = false
  }
}
const visible = ref()
const closeOverSide = () => {
  resetForm()
  visible.value = false
}

const openPopupConfirmDocument = ref(false)

const labelConfirmDocument = ref('')

const visibleCreate = async () => {
  const { expiredDate, type, healthResult } = dataFormEditDocument.value
  if (!['HEALTHCHECK', 'CONTRACT'].includes(type)) {
    return await handleSubmitFormEditDocument()
  }

  if (expiredDate && new Date(`${expiredDate} 23:59:59`).getTime() < new Date().getTime()) {
    if (type === 'HEALTHCHECK' && !healthResult) {
      return await handleSubmitFormEditDocument()
    }
    labelConfirmDocument.value = $t('labels.contentConfirmDocumentDriverExpiredDate')
    openPopupConfirmDocument.value = true
    return
  }

  if (!expiredDate) {
    if (!healthResult) {
      return await handleSubmitFormEditDocument()
    }

    labelConfirmDocument.value = $t('labels.contentConfirmExpiredDateEmpty')
    openPopupConfirmDocument.value = true
    return
  }

  await handleSubmitFormEditDocument()
}
</script>
